import React, { ReactNode, useState, useRef, useEffect } from 'react';
import { useAuth } from '@/features/auth';
import { LogOut, Shield, User, PanelLeft, Menu, ChevronDown, Settings, UserCircle } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
  title?: string;
  onMenuToggle?: () => void;
}

export default function Layout({ children, title = 'TurtleConnector', onMenuToggle }: LayoutProps) {
  const { user, logout } = useAuth();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getRoleDisplayName = (role: string, isLicensed?: boolean) => {
    switch (role) {
      case 'agent':
        return isLicensed ? 'Licensed Agent' : 'Sales Rep';
      case 'hr_manager':
        return 'HR Manager';
      case 'sales_manager':
        return 'Sales Manager';
      case 'fmo_ceo':
        return 'FMO CEO';
      case 'admin':
        return 'Admin';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'agent':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'hr_manager':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sales_manager':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'fmo_ceo':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'user':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getHeaderGradient = (role: string) => {
    // Unified professional gradient for all roles
    return 'bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 border-slate-300';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
      {/* Header - Responsive and Fixed */}
      <header className={`fixed top-0 left-0 right-0 z-20 shadow-lg border-b ${user ? getHeaderGradient(user.role) : 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200'}`}>
        <div className="w-full max-w-[100vw] px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">

            {/* Left Section - Logo and Title */}
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              {/* Mobile menu toggle button */}
              <button
                onClick={onMenuToggle}
                className="lg:hidden p-1.5 sm:p-2 rounded-md text-white hover:bg-white/10 transition-colors duration-200 flex-shrink-0"
                aria-label="Toggle menu"
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              {/* Logo */}
              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary-600 rounded-lg flex-shrink-0">
                <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>

              {/* Title Section */}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-bold text-white truncate">
                  <span className="sm:hidden">TC</span>
                  <span className="hidden sm:inline">TurtleConnector</span>
                </h1>
                {title && title !== 'TurtleConnector' && (
                  <p className="text-xs sm:text-sm text-slate-200 truncate hidden sm:block">
                    {title}
                  </p>
                )}
              </div>
            </div>

            {/* Right Section - User Dropdown */}
            <div className="flex items-center flex-shrink-0">
              {user && (
                <div className="relative" ref={dropdownRef}>
                  {/* User Dropdown Trigger */}
                  <button
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="flex items-center space-x-2 sm:space-x-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-200 group"
                  >
                    {/* User Avatar */}
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/80 flex items-center justify-center shadow-sm border border-white/50">
                      <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                    </div>

                    {/* User Info - Desktop Only */}
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-semibold text-white truncate max-w-32">
                        {user.email.split('@')[0]}
                      </div>
                      <div className="text-xs text-slate-200">
                        {getRoleDisplayName(user.role, user.isLicensed)}
                      </div>
                    </div>

                    {/* Dropdown Arrow */}
                    <ChevronDown className={`w-4 h-4 text-slate-200 transition-transform duration-200 ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 sm:w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <UserCircle className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {user.email}
                            </p>
                            <p className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getRoleBadgeColor(user.role)}`}>
                              {getRoleDisplayName(user.role, user.isLicensed)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {/* <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <Settings className="w-4 h-4 text-gray-500" />
                          <span>Settings</span>
                        </button>
                        
                        <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <User className="w-4 h-4 text-gray-500" />
                          <span>Profile</span>
                        </button> */}
                      </div>

                      {/* Logout Button */}
                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area - Responsive padding for header */}
      <main className="flex-1 w-full pt-14 sm:pt-16 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}